import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { NotifyService } from '../../services/notify.service';
import { LoginService } from '../../services/login.service';
import { UserInfoService } from 'src/app/shared-resources/services/user-info.service';
import { LayoutService } from 'src/app/shared-resources/services/layout.service';

@Component({
  selector: 'header-notify',
  template: `
    <div class="mr-md" nz-dropdown [nzDropdownMenu]="menu" nzTrigger="click" [(nzVisible)]="dropdownVisible" (nzVisibleChange)="onOpen($event)">
      <nz-badge [nzCount]="count" [nzSize]="'small'">
        <i nz-icon nzType="bell" nzTheme="outline" class="notify-bell-icon"></i>
      </nz-badge>
    </div>

    <nz-dropdown-menu #menu="nzDropdownMenu">
      <div class="notify-panel" (scroll)="onScroll($event)">
        <div class="notify-header">
          <span class="notify-title">Notifications({{ notificationCount }})</span>
          <nz-switch [(ngModel)]="unreadOnly" nzSize="small" nzCheckedChildren="Unread" nzUnCheckedChildren="All" (ngModelChange)="onToggle()"></nz-switch>
        </div>

        <ng-container *ngIf="loading && filtered.length === 0">
          <div class="notify-center"><nz-spin></nz-spin></div>
        </ng-container>
      

        <nz-list [nzDataSource]="filtered" [nzRenderItem]="itemTpl" [nzSplit]="true">
          <ng-template #itemTpl let-item>
            <nz-list-item class="notify-item" [class.unread]="!item.readAt" (click)="markRead(item)">
              <nz-list-item-meta [nzAvatar]="avatarTpl" [nzTitle]="item?.webChannel?.message || 'Notification'" [nzDescription]="descTpl">
              </nz-list-item-meta>
              <ng-template #descTpl>
                <div class="row-page">
                  <span>{{ formatDate(item?.webChannel?.sentTime) }}</span>
                  <a *ngIf="item?.metadata?.label" (click)="navigate($event, item)" class="notify-goto">Go to →</a>
                </div>
              </ng-template>
              <ng-template #avatarTpl>
                <nz-avatar nzIcon="notification" [ngStyle]="{'background-color': item.readAt ? '#d9d9d9' : '#1890ff'}"></nz-avatar>
              </ng-template>
            </nz-list-item>
          </ng-template>
        </nz-list>

        <div *ngIf="loading && filtered.length > 0" class="notify-center notify-loading-more">
          <nz-spin nzSize="small"></nz-spin>
        </div>

        <div *ngIf="!hasMore && filtered.length > 0" class="notify-center notify-end-msg">
          That's All!
        </div>
      </div>
    </nz-dropdown-menu>
  `,
  styles: [`
   .notify-panel {
  width: 400px;
  max-height: 530px;
  padding: 0 10px 10px;
  overflow-y: auto;

  background: #f7f8fc;
  border-radius: 14px;
  box-shadow: 0 16px 45px rgba(20, 25, 45, .16);
}

.notify-header {
  margin: 0 -10px 8px;
  padding: 18px 20px;

  background: #fff;
  border-radius: 14px 14px 0 0;
  border-bottom: 1px solid #eee;

  display: flex;
  justify-content: space-between;
  align-items: center;
}

.notify-item {
  margin: 8px 0;
  padding: 14px !important;

  background: #fff;
  border: 1px solid #e9eaf1;
  border-radius: 10px;

  cursor: pointer;
  transition: all .2s ease;
}

.notify-item:hover {
  transform: translateY(-1px);
  border-color: #c9c4ff;
  box-shadow: 0 5px 16px rgba(80, 65, 180, .08);
}

.notify-item.unread {
  background: linear-gradient(
    135deg,
    #f8f5ff,
    #fff
  );

  border-left: 3px solid #0077a3;
  border-color: #ddd5ff;
}

.notify-item.unread nz-avatar {
  background: #0077a3 !important;
}

.notify-item:not(.unread) nz-avatar {
  background: #f0f0f3 !important;
  color: #8c8c8c;
}

.notify-footer {
  margin: 8px -10px -10px;
  padding: 15px 20px;

  background: #f2efff;
  border-radius: 0 0 14px 14px;

  color: #6741d9;
  font-weight: 600;
  text-align: center;
}

.notify-bell-icon {
  font-size: 20px;
  color: #ffffff;
}

.notify-title {
  font-weight: 600;
  font-size: 16px;
}

.notify-goto {
  font-size: 12px;
}

.notify-loading-more {
  padding: 12px;
}

.notify-end-msg {
  padding: 10px;
  color: #999;
  font-size: 12px;
}
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderNotifyComponent implements OnInit, OnDestroy {
  count = 0;
  dropdownVisible = false;
  allNotifications: any[] = [];
  loading = false;
  hasMore = false;
  unreadOnly = true;
  private page = 0;
  private readonly pageSize = 10;
  private subs = new Subscription();

  get filtered(): any[] {
    return this.unreadOnly ? this.allNotifications.filter(n => !n.readAt) : this.allNotifications;
  }

  get notificationCount(): number {
    return this.unreadOnly ? this.allNotifications.filter(n => !n.readAt).length : this.allNotifications.length;
  }

  constructor(
    private notifyService: NotifyService,
    private loginService: LoginService,
    private userInfo: UserInfoService,
    private layoutService: LayoutService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.subs.add(this.notifyService.count$.subscribe(c => { this.count = c; this.cdr.markForCheck(); }));
    this.subs.add(this.notifyService.messages$.subscribe(msg => {
      this.allNotifications = [msg, ...this.allNotifications];
      this.cdr.markForCheck();
    }));
  }

  onOpen(visible: boolean): void {
    if (!visible) return;
    this.page = 0;
    this.allNotifications = [];
    this.fetchNotifications();
  }

  onToggle(): void {
    this.page = 0;
    this.allNotifications = [];
    this.fetchNotifications();
  }

  markRead(item: any): void {
    if (item.readAt) return;
    item.readAt = new Date().toISOString();
    this.loginService.markNotificationRead(item.id).subscribe({
      next: () => {
        this.loginService.getUnreadCount((this.userInfo.getUserInfo() as any)?.userInfoId).subscribe(
          (res: any) => this.notifyService.setCount(res?.count ?? res ?? 0)
        );
        this.cdr.markForCheck();
      }
    });
  }

  onScroll(event: Event): void {
    if (!this.hasMore || this.loading) return;
    const el = event.target as HTMLElement;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) {
      this.fetchNotifications();
    }
  }

  private fetchNotifications(): void {
    const userId = (this.userInfo.getUserInfo() as any)?.userInfoId;
    if (!userId) return;
    this.loading = true;
    this.cdr.markForCheck();
    const isRead = this.unreadOnly ? false : undefined;
    this.loginService.getNotifications(userId, this.page, this.pageSize, isRead).subscribe({
      next: (res: any) => {
        const items: any[] = res?.content ?? [];
        this.allNotifications = [...this.allNotifications, ...items];
        this.page++;
        this.hasMore = !res?.last;
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: () => { this.loading = false; this.cdr.markForCheck(); }
    });
  }

  navigate(event: MouseEvent, item: any): void {
    event.stopPropagation();
    this.markRead(item);
    const label = (item?.metadata?.label as string)?.replace(/\/+$/, '');
    const id = item?.metadata?.data?.id;
    const url = id ? `${label}?id=${id}` : label;
    this.dropdownVisible = false;
    this.layoutService.navigateTo(url);
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    const isYesterday = date.toDateString() === yesterday.toDateString();
    const time = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    if (isToday) return `Today, ${time}`;
    if (isYesterday) return `Yesterday, ${time}`;
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) + ', ' + time;
  }

  ngOnDestroy(): void { this.subs.unsubscribe(); }
}
